import { useCallback, useEffect, useRef } from 'react';

const useScopedRequestGate = (boundary) => {
    const boundaryRef = useRef(boundary);
    const boundaryGenerationRef = useRef(0);
    const scopeGenerationsRef = useRef(new Map());
    const mountedRef = useRef(true);
    const instanceRef = useRef(Symbol('scoped-request-gate'));

    if (!Object.is(boundaryRef.current, boundary)) {
        boundaryRef.current = boundary;
        boundaryGenerationRef.current += 1;
        scopeGenerationsRef.current.clear();
    }

    useEffect(() => {
        const scopeGenerations = scopeGenerationsRef.current;
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            boundaryGenerationRef.current += 1;
            scopeGenerations.clear();
        };
    }, []);

    const beginRequest = useCallback((scope = 'default') => {
        const generation = (scopeGenerationsRef.current.get(scope) || 0) + 1;
        scopeGenerationsRef.current.set(scope, generation);
        return {
            instance: instanceRef.current,
            boundary: boundaryRef.current,
            boundaryGeneration: boundaryGenerationRef.current,
            scope,
            generation,
        };
    }, []);

    const isCurrentRequest = useCallback((request) => (
        mountedRef.current
        && request?.instance === instanceRef.current
        && request?.boundary === boundaryRef.current
        && request?.boundaryGeneration === boundaryGenerationRef.current
        && scopeGenerationsRef.current.get(request?.scope) === request?.generation
    ), []);

    return { beginRequest, isCurrentRequest };
};

export default useScopedRequestGate;
